import React, { useMemo } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useAppSelector } from '../../redux/helper';
import { selectQuantityButtons } from '../../redux/SettingsSlice';
import { useTheme, GTFonts } from '../../theme';
import { fs, s, v } from '../../utils/libs';
import Typography from './Typography';

type QuantityButtonVariant = 'plus' | 'minus';

interface QuantityControlButtonProps {
  variant: QuantityButtonVariant;
  onPress: () => void;
  disabled?: boolean;
  /** Show disabled styling but keep button pressable (for out-of-stock with snackbar) */
  showDisabledStyle?: boolean;
  /** Use web layout (Pressable with hover). Auto-detected from Platform.OS if not provided. */
  useWebLayout?: boolean;
  /** Custom size multiplier for tablet adjustments */
  compact?: boolean;
}

const QuantityControlButton: React.FC<QuantityControlButtonProps> = ({
  variant,
  onPress,
  disabled = false,
  showDisabledStyle = false,
  useWebLayout: useWebLayoutProp,
  compact = false,
}) => {
  const { theme } = useTheme();
  const quantityButtons = useAppSelector(selectQuantityButtons);

  const useWebLayout = useWebLayoutProp ?? Platform.OS === 'web';

  type ColorKey = keyof typeof theme.colors;

  const resolveColor = (key: string | undefined, fallback: ColorKey) => {
    const k = (key || '') as ColorKey;
    return theme.colors[k] ?? theme.colors[fallback];
  };

  const resolveLightForMain = (
    mainKey: string | undefined,
    fallback: ColorKey,
  ) => {
    const main = (mainKey || '') as ColorKey;
    const lightKey = (main + 'Light') as ColorKey;
    return theme.colors[lightKey] ?? theme.colors[fallback];
  };

  const colors = useMemo(() => {
    const btnSettings = (quantityButtons as any)?.[variant];
    if (variant === 'plus') {
      return {
        bg: resolveLightForMain(btnSettings?.main, 'successLight'),
        bgActive: resolveColor(btnSettings?.main, 'success'),
        text: resolveColor(btnSettings?.main, 'success'),
        textActive: theme.colors.surface,
        bgDisabled: theme.colors.borderGray,
        // Primary text on grey — matches old Button out-of-stock look (dark on grey)
        textDisabled: theme.colors.text,
      };
    }
    return {
      bg: resolveLightForMain(btnSettings?.main, 'errorLight'),
      bgActive: resolveColor(btnSettings?.main, 'error'),
      text: resolveColor(btnSettings?.main, 'error'),
      textActive: theme.colors.surface,
      bgDisabled: theme.colors.borderGray,
      textDisabled: theme.colors.text,
    };
  }, [quantityButtons, variant, theme]);

  const text = variant === 'plus' ? '+' : '-';
  const styles = useMemo(
    () => createStyles(theme, useWebLayout, compact),
    [theme, useWebLayout, compact],
  );

  // Use disabled styling if either truly disabled or showDisabledStyle is set
  const useDisabledStyle = disabled || showDisabledStyle;

  if (useWebLayout) {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={({ pressed, hovered }: any) => [
          styles.btn,
          { backgroundColor: useDisabledStyle ? colors.bgDisabled : colors.bg },
          !useDisabledStyle &&
            (pressed || hovered) && { backgroundColor: colors.bgActive },
        ]}>
        {({ pressed, hovered }: any) => (
          <Typography
            style={[
              styles.btnText,
              {
                color: useDisabledStyle
                  ? colors.textDisabled
                  : pressed || hovered
                    ? colors.textActive
                    : colors.text,
              },
            ]}>
            {text}
          </Typography>
        )}
      </Pressable>
    );
  }

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
      hitSlop={ s(20)}
      style={[
        styles.btn,
        { backgroundColor: useDisabledStyle ? colors.bgDisabled : colors.bg },
      ]}>
      <Typography
        style={[
          styles.btnText,
          { color: useDisabledStyle ? colors.textDisabled : colors.text },
        ]}>
        {text}
      </Typography>
    </TouchableOpacity>
  );
};

export default QuantityControlButton;

const createStyles = (theme: any, useWebLayout: boolean, compact: boolean) =>
  StyleSheet.create({
    btn: {
      paddingHorizontal: s(useWebLayout ? 14 : compact ? 7 : 10),
      paddingVertical: s(useWebLayout ? 6 : 4),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: s(useWebLayout ? 40 : compact ? 32 : 36),
      height: v(useWebLayout ? 37 : compact ? 26 : 31),
    },
    btnText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(useWebLayout ? 18 : 16),
      lineHeight: fs(useWebLayout ? 18 : 16),
    },
  });
